import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import { Save, X, Upload, FileText, Calendar, Users, User, ShieldCheck, ClipboardCheck, CreditCard, MinusCircle, Link2, Plus, Trash2, Pencil } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import FolderTabNav from '@/components/ui/folder-tab-nav';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';
import { normalizePhoneInput } from '@/lib/phone';
import { shortDescription } from '@/lib/driverDeductions';
import DriverPayArrangements from '@/components/DriverPayArrangements';
import DriverPaymentTypePercentages, { DriverPaymentTypePercentagesHandle } from '@/components/DriverPaymentTypePercentages';
import { DeductionCategory, loadDeductionCategories, PaymentCategory, loadPaymentCategories } from '@/lib/settingsConfig';
import { useBillableUnitsGuard } from '@/hooks/useBillableUnitsGuard';
import BillableUnitsWarningDialog from '@/components/BillableUnitsWarningDialog';

type PaymentType = 'Waged' | 'Commission' | 'Contractor';
type DriverGender = 'Male' | 'Female' | 'Other' | 'PreferNotToSay';

const NO_SELECTION = '__none__';

interface ShiftTypeOption {
  code: string;
  label: string;
  default_colour_hex: string | null;
  is_active: boolean;
}

interface DriverGroupOption {
  id: string;
  name: string;
  colour_hex: string | null;
  is_active: boolean;
}

interface DriverTypeOption {
  id: string;
  type_key: string;
  type_name: string;
  default_commission_percent: number | null;
  uses_bonus_tiers: boolean;
  uses_payment_type_percentages: boolean;
  allows_deductions: boolean;
  is_waged?: boolean;
  is_commission?: boolean;
  is_contractor?: boolean;
  is_leased?: boolean;
  is_active: boolean;
}

interface TenantOption {
  id: string;
  tenant_name: string;
  company_login_code?: string | null;
}

interface AppModuleOption {
  module_key: string;
  module_name: string;
  sort_order: number;
}

interface TenantUserRecord {
  id: string;
  tenant_id: string;
  user_id: string;
  driver_name?: string | null;
  display_name?: string | null;
  email?: string | null;
  is_active?: boolean | null;
}

interface ModulePermissionOverride {
  id?: string;
  tenant_user_id?: string;
  module_key: string;
  can_view: boolean;
  can_edit: boolean;
  can_approve?: boolean;
  can_export?: boolean;
  is_active?: boolean;
}

interface DriverFinancialAdjustment {
  id: string;
  driver_name: string;
  adjustment_type: 'deduction' | 'credit';
  category: string;
  amount: number;
  total_amount: number | null;
  remaining_balance: number | null;
  deduction_status?: 'active' | 'paid' | 'cancelled' | 'adjusted' | null;
  adjustment_mode: 'one_off' | 'recurring' | 'reducing_balance';
  apply_per_payout: number | null;
  minimum_net_pay: number | null;
  auto_apply: boolean;
  priority: number;
  active: boolean;
  starts_on: string | null;
  ends_on: string | null;
  /** Recurring only: take a share of the weekly amount when it starts/ends part-way through a pay week. */
  prorate_partial_weeks?: boolean | null;
  applied: boolean | null;
  description: string | null;
  /** Per-deduction choice, independent of the category's payout_mode: does
   *  THIS specific deduction pay to an external bank account (a 3rd party,
   *  e.g. court fines) rather than just reducing net pay back to the
   *  company. Only when true do the three fields below apply -- so the same
   *  category (e.g. Structured Repayment) can mix company-repayment
   *  deductions with 3rd-party-payable ones. */
  requires_bank_account: boolean;
  bank_account_number: string | null;
  bank_account_name: string | null;
  payment_reference: string | null;
}

interface DriverRelationshipRecord {
  id: string;
  parent_driver_name: string;
  child_driver_name: string;
  relationship_type: string;
  description: string | null;
  can_view_roster: boolean;
  can_edit_roster: boolean;
  can_view_payroll: boolean;
  can_edit_payroll: boolean;
  can_view_driver_details: boolean;
  can_edit_driver_details: boolean;
  is_active: boolean;
}

interface DriverNameOption {
  driver_name: string;
}

type TaxTreatment = 'PAYE' | 'GST_REGISTERED' | 'GST_EXEMPT' | 'OTHER';


interface FullDriver {
  driver_name: string;
  ridewise_id: string | null;
  phone: string | null;
  email: string | null;
  AdminRights: string | null;
  created_at: string | null;
  updated_at: string | null;
  Password: string | null;
  driver_lastname: string | null;
  driver_firstname: string | null;
  gender?: DriverGender | null;
  dob?: string | null;
  include_in_reports: boolean | null;
  bank_account_number: string | null;
  bank_account_name: string | null;
  payout_reference_override: string | null;
  insurance_start_date: string | null;
  insurance_last_claim_date: string | null;
  insurance_claim_free: boolean | null;
  last_vehicle_inspection_at: string | null;
  last_vehicle_inspection_shift_date: string | null;
  vehicle_inspection_shift_counter: number | null;
  payment_type: PaymentType | null;
  is_active: boolean;
  can_login?: boolean | null;
  exempt_from_inspection?: boolean | null;
  toro?: boolean | null;
  lcp_units?: boolean | null;
  dl_number?: string | null;
  dl_version?: number | null;
  dl_expiry?: string | null;
  dl_class?: string | null;
  dl_issue_date?: string | null;
  dl_conditions?: string | null;
  convictions_last_7_years?: string | null;
  accidents_claims_last_5_years?: string | null;
  address?: string | null;
  id_expiry?: string | null;
  dl_days_left?: number | null;
  id_days_left?: number | null;
  dl_taxicaller_updated?: boolean | null;
  id_taxicaller_updated?: boolean | null;
  dl_scan_on_file?: boolean | null;
  id_scan_on_file?: boolean | null;
  dl_scan_path?: string | null;
  id_scan_path?: string | null;
  can_drive_manual?: boolean | null;
  tenant_id?: string | null;
  driver_type_id?: string | null;
  commission_percent_override?: number | null;
  tax_treatment?: TaxTreatment | null;
  gst_registered?: boolean | null;
  gst_number?: string | null;
  gst_registered_name?: string | null;
  gst_verified?: boolean | null;
  gst_verified_at?: string | null;
  tax_notes?: string | null;
  // Roster profile fields
  roster_positions?: string[] | null;
  roster_groups?: string[] | null;
  driver_tags?: string[] | null;
  roster_max_hours_per_week?: number | null;
  roster_preferred_vehicle?: string | null;
  roster_notes?: string | null;
}

interface DriverEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: FullDriver | null;
  onSaved: () => void;
}

const BUCKET = 'driver_documents';

const PAYMENT_TYPE_OPTIONS: { value: PaymentType | typeof NO_SELECTION; label: string }[] = [
  { value: NO_SELECTION, label: '— Select —' },
  { value: 'Waged', label: 'Waged' },
  { value: 'Commission', label: 'Commission' },
  { value: 'Contractor', label: 'Contractor' },
];

const GENDER_OPTIONS: { value: DriverGender | typeof NO_SELECTION; label: string }[] = [
  { value: NO_SELECTION, label: '— Select —' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
  { value: 'PreferNotToSay', label: 'Prefer not to say' },
];

const TRANSMISSION_OPTIONS: { value: string; label: string }[] = [
  { value: NO_SELECTION, label: 'Not Set' },
  { value: 'auto_only', label: 'Automatic Only' },
  { value: 'auto_manual', label: 'Automatic & Manual' },
];

const TAX_TREATMENT_OPTIONS: { value: TaxTreatment; label: string }[] = [
  { value: 'PAYE', label: 'PAYE / Standard' },
  { value: 'GST_REGISTERED', label: 'GST Registered' },
  { value: 'GST_EXEMPT', label: 'GST Exempt' },
  { value: 'OTHER', label: 'Other' },
];

// ROSTER_GROUP_OPTIONS is now loaded from DB - kept as fallback only
const ROSTER_GROUP_OPTIONS_FALLBACK = [
  'Driver Team',
  'Night Crew',
  'Weekend Team',
  'STS Team',
  'TMV Team',
  'Relief Pool',
  'Training',
];

// Matches TaxiCaller's driver profile tags, excluding ones already covered
// by dedicated fields here (gender covers Male/Female; payment_type covers
// Waged/Commission/Contractor).
const DRIVER_TAG_OPTIONS = ['Light Courier', 'Heavy Courier', 'ACC', 'DHB', 'Renal', 'COVID+', 'Able Helper'];


const toSelectValue = (val: string | null | undefined): string => {
  if (!val || val === '') return NO_SELECTION;
  return val;
};

const fromSelectValue = (val: string): string | null => {
  if (val === NO_SELECTION) return null;
  return val;
};

const transmissionToSelectValue = (val: boolean | null | undefined): string => {
  if (val === null || val === undefined) return NO_SELECTION;
  return val ? 'auto_manual' : 'auto_only';
};

const transmissionFromSelectValue = (val: string): boolean | null => {
  if (val === NO_SELECTION) return null;
  return val === 'auto_manual';
};

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="rounded-lg border border-gray-700/60 bg-gray-900/30 p-4">
    <div className="mb-3">
      <div className="text-sm font-semibold text-yellow-500">{title}</div>
      {subtitle ? <div className="text-xs text-gray-400 mt-1">{subtitle}</div> : null}
    </div>
    {children}
  </div>
);

const DriverEditModal: React.FC<DriverEditModalProps> = ({ isOpen, onClose, driver, onSaved }) => {
  const [editData, setEditData] = useState<Partial<FullDriver>>({});
  const [dlFile, setDlFile] = useState<File | null>(null);
  const [idFile, setIdFile] = useState<File | null>(null);

  const [uploading, setUploading] = useState<{ dl: boolean; id: boolean }>({ dl: false, id: false });
  const [saving, setSaving] = useState(false);
  const paymentPercentRef = useRef<DriverPaymentTypePercentagesHandle>(null);
  const { checkChange: checkBillableUnitsChange, dialogProps: billableUnitsDialogProps } = useBillableUnitsGuard();

  const [activeTab, setActiveTab] = useState('details');
  const [shiftTypes, setShiftTypes] = useState<ShiftTypeOption[]>([]);
  const [vehicles, setVehicles] = useState<{ vehicle_number: string }[]>([]);
  const [dbGroups, setDbGroups] = useState<DriverGroupOption[]>([]);
  const [driverTypes, setDriverTypes] = useState<DriverTypeOption[]>([]);
  // How many dated pay-arrangement changes the driver has; when there are any, the history
  // below (not the plain Driver Type picker) decides what type they're on each day.
  const [payHistoryCount, setPayHistoryCount] = useState(0);
  const [tenantInfo, setTenantInfo] = useState<TenantOption | null>(null);
  const [appModules, setAppModules] = useState<AppModuleOption[]>([]);
  const [tenantUser, setTenantUser] = useState<TenantUserRecord | null>(null);
  const [modulePermissions, setModulePermissions] = useState<Record<string, ModulePermissionOverride>>({});
  const [deductions, setDeductions] = useState<DriverFinancialAdjustment[]>([]);
  const [deductionCategories, setDeductionCategories] = useState<DeductionCategory[]>([]);
  const [paymentCategories, setPaymentCategories] = useState<PaymentCategory[]>([]);
  const [showDeductionForm, setShowDeductionForm] = useState(false);
  const [editingDeductionId, setEditingDeductionId] = useState<string | null>(null);
  const [newDeduction, setNewDeduction] = useState<Partial<DriverFinancialAdjustment>>({
    adjustment_type: 'deduction',
    category: 'manual',
    adjustment_mode: 'one_off',
    amount: 0,
    active: true,
    auto_apply: true,
    priority: 100,
  });
  const [relationships, setRelationships] = useState<DriverRelationshipRecord[]>([]);
  const [allDriverNames, setAllDriverNames] = useState<DriverNameOption[]>([]);
  const [showRelationshipForm, setShowRelationshipForm] = useState(false);
  const [newRelationship, setNewRelationship] = useState<Partial<DriverRelationshipRecord>>({
    relationship_type: 'linked',
    can_view_roster: true,
    can_edit_roster: false,
    can_view_payroll: false,
    can_edit_payroll: false,
    can_view_driver_details: true,
    can_edit_driver_details: false,
    is_active: true,
  });

  useEffect(() => {
    if (driver && isOpen) {
      loadDriverData(driver);
      loadShiftTypes();
      loadVehicles();
      loadDriverGroups();
      loadAppModules();
      loadDriverTypes(driver.tenant_id || null);
      loadDeductions(driver.driver_name);
      loadDeductionCategories(driver.tenant_id || null).then(setDeductionCategories);
      loadPaymentCategories(driver.tenant_id || null).then(setPaymentCategories);
      loadRelationships(driver);
      loadAllDriverNames(driver.tenant_id || null);
    }
  }, [driver, isOpen]);


  const loadShiftTypes = async () => {
    const { data } = await supabase
      .from('roster_shift_types')
      .select('code, label, default_colour_hex, is_active')
      .eq('is_active', true)
      .order('sort_order');
    if (data) setShiftTypes(data);
  };

  const loadVehicles = async () => {
    const { data } = await supabase
      .from('vehicles')
      .select('vehicle_number')
      .order('vehicle_number');
    if (data) setVehicles(data);
  };

  const loadDriverGroups = async () => {
    const { data } = await supabase
      .from('roster_driver_groups')
      .select('id, name, colour_hex, is_active')
      .eq('is_active', true)
      .order('sort_order');
    if (data) setDbGroups(data);
  };

  const loadDriverTypes = async (tenantId?: string | null) => {
    let query = supabase
      .from('driver_types')
      .select('id, type_key, type_name, default_commission_percent, uses_bonus_tiers, uses_payment_type_percentages, allows_deductions, is_waged, is_commission, is_contractor, is_leased, is_active')
      .eq('is_active', true)
      .order('type_name');

    if (tenantId) query = query.eq('tenant_id', tenantId);

    const { data, error } = await query;
    if (error) {
      console.error('Error loading driver types:', error);
      setDriverTypes([]);
      return;
    }

    setDriverTypes((data as DriverTypeOption[]) || []);
  };

  const loadTenantInfo = async (tenantId?: string | null) => {
    if (!tenantId) {
      setTenantInfo(null);
      return;
    }

    const { data, error } = await supabase
      .from('tenants')
      .select('id, tenant_name, company_login_code')
      .eq('id', tenantId)
      .maybeSingle();

    if (error) {
      console.error('Error loading tenant info:', error);
      setTenantInfo(null);
      return;
    }

    setTenantInfo(data as TenantOption);
  };


  const loadAppModules = async () => {
    const { data, error } = await supabase
      .from('app_modules')
      .select('module_key, module_name, sort_order')
      .eq('is_active', true)
      .order('sort_order');

    if (error) {
      console.error('Error loading app modules:', error);
      setAppModules([]);
      return;
    }

    setAppModules((data as AppModuleOption[]) || []);
  };

  const loadTenantUserAndPermissions = async (tenantId: string | null | undefined, d: FullDriver) => {
    if (!tenantId || !d.driver_name) {
      setTenantUser(null);
      setModulePermissions({});
      return;
    }

    const { data: tuData, error: tuError } = await supabase
      .from('tenant_users')
      .select('id, tenant_id, user_id, driver_name, display_name, email, is_active')
      .eq('tenant_id', tenantId)
      .eq('driver_name', d.driver_name)
      .maybeSingle();

    if (tuError) {
      console.error('Error loading tenant user:', tuError);
      setTenantUser(null);
      setModulePermissions({});
      return;
    }

    const loadedTenantUser = tuData as TenantUserRecord | null;
    setTenantUser(loadedTenantUser);

    if (!loadedTenantUser?.id) {
      setModulePermissions({});
      return;
    }

    const { data: permData, error: permError } = await supabase
      .from('tenant_user_module_permissions')
      .select('id, tenant_user_id, module_key, can_view, can_edit, can_approve, can_export, is_active')
      .eq('tenant_user_id', loadedTenantUser.id)
      .eq('is_active', true);

    if (permError) {
      console.error('Error loading user module permissions:', permError);
      setModulePermissions({});
      return;
    }

    const next: Record<string, ModulePermissionOverride> = {};
    ((permData as ModulePermissionOverride[]) || []).forEach((row) => {
      next[row.module_key] = {
        ...row,
        can_view: !!row.can_view,
        can_edit: !!row.can_edit,
        can_approve: !!row.can_approve,
        can_export: !!row.can_export,
      };
    });
    setModulePermissions(next);
  };

  const loadDeductions = async (driverName: string) => {
    const { data, error } = await supabase
      .from('driver_financial_adjustments')
      .select('id, driver_name, adjustment_type, category, amount, total_amount, remaining_balance, adjustment_mode, apply_per_payout, minimum_net_pay, auto_apply, priority, active, starts_on, ends_on, prorate_partial_weeks, applied, description, deduction_status')
      .eq('driver_name', driverName)
      .order('active', { ascending: false })
      .order('priority', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading driver financial adjustments:', error);
      setDeductions([]);
      return;
    }

    setDeductions((data as DriverFinancialAdjustment[]) || []);
  };

  const loadRelationships = async (d: FullDriver) => {
    const tenantId = d.tenant_id;
    if (!tenantId) {
      setRelationships([]);
      return;
    }

    const { data, error } = await supabase
      .from('driver_relationships')
      .select('id, parent_driver_name, child_driver_name, relationship_type, description, can_view_roster, can_edit_roster, can_view_payroll, can_edit_payroll, can_view_driver_details, can_edit_driver_details, is_active')
      .eq('tenant_id', tenantId)
      .or(`parent_driver_name.eq.${d.driver_name},child_driver_name.eq.${d.driver_name}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading driver relationships:', error);
      setRelationships([]);
      return;
    }

    setRelationships((data as DriverRelationshipRecord[]) || []);
  };

  const loadAllDriverNames = async (tenantId?: string | null) => {
    let query = supabase
      .from('drivers')
      .select('driver_name')
      .order('driver_name');

    if (tenantId) query = query.eq('tenant_id', tenantId);

    const { data, error } = await query;
    if (error) {
      console.error('Error loading driver names:', error);
      setAllDriverNames([]);
      return;
    }

    setAllDriverNames((data as DriverNameOption[]) || []);
  };

  const loadDriverData = async (d: FullDriver) => {

    const { data, error } = await supabase
      .from('drivers')
      .select('tenant_id, driver_type_id, commission_percent_override, tax_treatment, gst_registered, gst_number, gst_registered_name, gst_verified, gst_verified_at, tax_notes, dl_scan_path, id_scan_path, dl_scan_on_file, id_scan_on_file, roster_positions, roster_groups, driver_tags, roster_max_hours_per_week, roster_preferred_vehicle, roster_notes, address, dl_class, dl_issue_date, dl_conditions, convictions_last_7_years, accidents_claims_last_5_years')
      .eq('driver_name', d.driver_name)
      .single();

    if (error) {
      console.error('Error fetching driver data:', error);
      setEditData({ ...d });
    } else {
      const dlScanPath = data?.dl_scan_path ?? d.dl_scan_path ?? null;
      const idScanPath = data?.id_scan_path ?? d.id_scan_path ?? null;
      
      setEditData({
        ...d,
        dl_scan_path: dlScanPath,
        id_scan_path: idScanPath,
        dl_scan_on_file: dlScanPath ? true : (data?.dl_scan_on_file ?? d.dl_scan_on_file ?? false),
        id_scan_on_file: idScanPath ? true : (data?.id_scan_on_file ?? d.id_scan_on_file ?? false),
        tenant_id: data?.tenant_id ?? d.tenant_id ?? null,
        driver_type_id: data?.driver_type_id ?? d.driver_type_id ?? null,
        commission_percent_override: data?.commission_percent_override ?? d.commission_percent_override ?? null,
        tax_treatment: data?.tax_treatment ?? d.tax_treatment ?? 'PAYE',
        gst_registered: data?.gst_registered ?? d.gst_registered ?? false,
        gst_number: data?.gst_number ?? d.gst_number ?? null,
        gst_registered_name: data?.gst_registered_name ?? d.gst_registered_name ?? null,
        gst_verified: data?.gst_verified ?? d.gst_verified ?? false,
        gst_verified_at: data?.gst_verified_at ?? d.gst_verified_at ?? null,
        tax_notes: data?.tax_notes ?? d.tax_notes ?? null,
        roster_positions: data?.roster_positions ?? d.roster_positions ?? [],
        roster_groups: data?.roster_groups ?? d.roster_groups ?? [],
        driver_tags: data?.driver_tags ?? d.driver_tags ?? [],
        roster_max_hours_per_week: data?.roster_max_hours_per_week ?? d.roster_max_hours_per_week ?? null,
        roster_preferred_vehicle: data?.roster_preferred_vehicle ?? d.roster_preferred_vehicle ?? null,
        roster_notes: data?.roster_notes ?? d.roster_notes ?? null,
        address: data?.address ?? d.address ?? null,
        dl_class: data?.dl_class ?? d.dl_class ?? null,
        dl_issue_date: data?.dl_issue_date ?? d.dl_issue_date ?? null,
        dl_conditions: data?.dl_conditions ?? d.dl_conditions ?? null,
        convictions_last_7_years: data?.convictions_last_7_years ?? d.convictions_last_7_years ?? null,
        accidents_claims_last_5_years: data?.accidents_claims_last_5_years ?? d.accidents_claims_last_5_years ?? null,
      });

      const resolvedTenantId = data?.tenant_id ?? d.tenant_id ?? null;
      await loadTenantInfo(resolvedTenantId);
      await loadDriverTypes(resolvedTenantId);
      await loadTenantUserAndPermissions(resolvedTenantId, { ...d, tenant_id: resolvedTenantId });
      await loadAllDriverNames(resolvedTenantId);
      await loadRelationships({ ...d, tenant_id: resolvedTenantId });
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    return String(dateStr).split('T')[0];
  };

  const toDateTimeLocal = (iso: string | null | undefined) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const fromDateTimeLocal = (val: string) => {
    if (!val) return null;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const calcAge = (dobStr: string | null | undefined): number | null => {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const formatAgeLabel = (age: number | null) => {
    if (age === null) return '-';
    if (age < 0 || age > 120) return 'Check DOB';
    return String(age);
  };

  const urgencyForDays = (days: number | null | undefined) => {
    if (days === null || days === undefined || typeof days !== 'number') return 'unknown';
    if (days < 30) return 'red';
    if (days < 90) return 'orange';
    return 'green';
  };

  const formatDaysLeft = (days: number | null | undefined) => {
    if (days === null || days === undefined || typeof days !== 'number') return '-';
    if (days < 0) return `Expired (${days})`;
    return `${days}`;
  };

  const openScan = (path: string | null | undefined) => {
    if (!path) return;
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    const publicUrl = data?.publicUrl;
    if (!publicUrl) {
      toast({ title: 'Error', description: 'Could not generate public URL', variant: 'destructive' });
      return;
    }
    window.open(publicUrl, '_blank', 'noopener,noreferrer');
  };

  const uploadDriverDoc = async (type: 'dl' | 'id') => {
    if (!driver) return;

    const file = type === 'dl' ? dlFile : idFile;
    if (!file) {
      toast({ title: 'Error', description: `Please choose a ${type.toUpperCase()} file first`, variant: 'destructive' });
      return;
    }

    setUploading((p) => ({ ...p, [type]: true }));

    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const driverSlug = driver.driver_name.replace(/[^a-zA-Z0-9_-]/g, '_');
      const path = `drivers/${driverSlug}/${type.toUpperCase()}_${Date.now()}_${safeName}`;

      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true });
      if (upErr) throw upErr;

      const updatePayload =
        type === 'dl'
          ? { dl_scan_path: path, dl_scan_on_file: true, dl_taxicaller_updated: false, updated_at: new Date().toISOString() }
          : { id_scan_path: path, id_scan_on_file: true, id_taxicaller_updated: false, updated_at: new Date().toISOString() };

      const { error: dbErr } = await supabase.from('drivers').update(updatePayload).eq('driver_name', driver.driver_name);
      if (dbErr) throw dbErr;

      setEditData((prev) => ({
        ...prev,
        ...(type === 'dl'
          ? { dl_scan_path: path, dl_scan_on_file: true, dl_taxicaller_updated: false }
          : { id_scan_path: path, id_scan_on_file: true, id_taxicaller_updated: false }),
      }));

      toast({ title: 'Uploaded', description: `${type.toUpperCase()} scan uploaded and linked to driver` });

      if (type === 'dl') setDlFile(null);
      if (type === 'id') setIdFile(null);
    } catch (e: any) {
      console.error('Upload error:', e);
      toast({ title: 'Upload failed', description: e?.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setUploading((p) => ({ ...p, [type]: false }));
    }
  };

  // Toggle a position code in the roster_positions array
  const togglePosition = (code: string) => {
    const current = editData.roster_positions || [];
    if (current.includes(code)) {
      setEditData({ ...editData, roster_positions: current.filter(c => c !== code) });
    } else {
      setEditData({ ...editData, roster_positions: [...current, code] });
    }
  };

  // Toggle a group in the roster_groups array
  const toggleGroup = (group: string) => {
    const current = editData.roster_groups || [];
    if (current.includes(group)) {
      setEditData({ ...editData, roster_groups: current.filter(g => g !== group) });
    } else {
      setEditData({ ...editData, roster_groups: [...current, group] });
    }
  };

  // Toggle a tag in the driver_tags array
  const toggleTag = (tag: string) => {
    const current = editData.driver_tags || [];
    if (current.includes(tag)) {
      setEditData({ ...editData, driver_tags: current.filter(t => t !== tag) });
    } else {
      setEditData({ ...editData, driver_tags: [...current, tag] });
    }
  };


  const toggleModulePermission = (moduleKey: string, field: 'can_view' | 'can_edit', value: boolean) => {
    setModulePermissions((prev) => {
      const current = prev[moduleKey] || { module_key: moduleKey, can_view: false, can_edit: false, can_approve: false, can_export: false, is_active: true };
      const next = { ...current, [field]: value };
      if (field === 'can_edit' && value) next.can_view = true;
      if (field === 'can_view' && !value) next.can_edit = false;
      return { ...prev, [moduleKey]: next };
    });
  };

  // tenant_users rows are only ever created server-side (by the driver-login
  // Edge Function on first real login, or by the one-time backfill script)
  // where a real auth.users id is available. This modal runs as an
  // anon/authenticated client and has no business minting auth accounts, so
  // it only looks up an existing row -- it used to blind-insert one without
  // a user_id, which silently defaulted to a meaningless random uuid that
  // matched no real login.
  const ensureTenantUser = async (): Promise<TenantUserRecord | null> => {
    if (tenantUser?.id) return tenantUser;
    return null;
  };

  const saveModulePermissions = async () => {
    if ((editData.AdminRights || driver?.AdminRights || 'No') !== 'Yes') return;
    const tu = await ensureTenantUser();
    if (!tu?.id) {
      toast({
        title: 'Cannot save permissions yet',
        description: `${driver?.driver_name || 'This driver'} hasn't logged in yet, so there's no account to attach module permissions to. Permissions can be set after their first login.`,
        variant: 'destructive',
      });
      return;
    }

    const rows = appModules.map((module) => {
      const perm = modulePermissions[module.module_key] || { module_key: module.module_key, can_view: false, can_edit: false };
      return {
        tenant_user_id: tu.id,
        module_key: module.module_key,
        can_view: !!perm.can_view,
        can_edit: !!perm.can_edit,
        can_approve: !!perm.can_approve,
        can_export: !!perm.can_export,
        is_active: true,
      };
    });

    const { error } = await supabase
      .from('tenant_user_module_permissions')
      .upsert(rows, { onConflict: 'tenant_user_id,module_key' });

    if (error) throw error;
  };

  const resetDeductionForm = () => {
    setEditingDeductionId(null);
    setShowDeductionForm(false);
    setNewDeduction({ adjustment_type: 'deduction', category: 'manual', adjustment_mode: 'one_off', amount: 0, active: true, auto_apply: true, priority: 100, requires_bank_account: false });
  };

  const startEditDeduction = (item: DriverFinancialAdjustment) => {
    setEditingDeductionId(item.id);
    setNewDeduction({
      adjustment_type: item.adjustment_type,
      category: item.category,
      adjustment_mode: item.adjustment_mode,
      amount: item.amount,
      total_amount: item.total_amount,
      remaining_balance: item.remaining_balance,
      apply_per_payout: item.apply_per_payout,
      minimum_net_pay: item.minimum_net_pay,
      auto_apply: item.auto_apply,
      priority: item.priority,
      active: item.active,
      starts_on: item.starts_on,
      ends_on: item.ends_on,
      prorate_partial_weeks: item.prorate_partial_weeks,
      description: item.description,
      requires_bank_account: item.requires_bank_account,
      bank_account_number: item.bank_account_number,
      bank_account_name: item.bank_account_name,
      payment_reference: item.payment_reference,
    });
    setShowDeductionForm(true);
  };

  const addDeduction = async () => {
    if (!driver) return;
    const tenantId = editData.tenant_id || driver.tenant_id;
    const mode = (newDeduction.adjustment_mode || 'one_off') as DriverFinancialAdjustment['adjustment_mode'];
    const amount = Number(newDeduction.amount || 0);
    const applyPerPayout = newDeduction.apply_per_payout === null || newDeduction.apply_per_payout === undefined ? null : Number(newDeduction.apply_per_payout);
    const totalAmount = newDeduction.total_amount === null || newDeduction.total_amount === undefined ? null : Number(newDeduction.total_amount);

    if (!amount || amount <= 0) {
      toast({ title: 'Deduction amount needed', description: 'Please enter an amount greater than zero.', variant: 'destructive' });
      return;
    }

    const category = newDeduction.category || 'manual';
    const requiresBankAccount = newDeduction.adjustment_type !== 'credit' && !!newDeduction.requires_bank_account;

    if (requiresBankAccount && !newDeduction.bank_account_number?.trim()) {
      toast({
        title: 'Bank account needed',
        description: `"Bank account required" is ticked -- enter the account this deduction pays to.`,
        variant: 'destructive',
      });
      return;
    }

    const payload = {
      tenant_id: tenantId || null,
      driver_name: driver.driver_name,
      adjustment_type: newDeduction.adjustment_type || 'deduction',
      category,
      amount,
      total_amount: mode === 'reducing_balance' ? (totalAmount || amount) : totalAmount,
      remaining_balance: mode === 'reducing_balance' ? (newDeduction.remaining_balance ?? totalAmount ?? amount) : newDeduction.remaining_balance ?? null,
      adjustment_mode: mode,
      apply_per_payout: mode === 'one_off' ? null : (applyPerPayout || amount),
      minimum_net_pay: newDeduction.minimum_net_pay === null || newDeduction.minimum_net_pay === undefined ? null : Number(newDeduction.minimum_net_pay),
      auto_apply: newDeduction.auto_apply ?? true,
      priority: Number(newDeduction.priority || 100),
      active: newDeduction.active ?? true,
      starts_on: formatDate(newDeduction.starts_on || null) || null,
      ends_on: formatDate(newDeduction.ends_on || null) || null,
      prorate_partial_weeks: mode === 'recurring' && !!newDeduction.prorate_partial_weeks,
      applied: true,
      description: newDeduction.description || null,
      requires_bank_account: requiresBankAccount,
      bank_account_number: requiresBankAccount ? newDeduction.bank_account_number?.trim() || null : null,
      bank_account_name: requiresBankAccount ? newDeduction.bank_account_name?.trim() || null : null,
      payment_reference: requiresBankAccount ? newDeduction.payment_reference?.trim() || null : null,
    };

    // UPDATE when editing an existing adjustment, otherwise INSERT a new one.
    // Either way the change immediately flows into the driver's next summary
    // because the summary reads live from driver_financial_adjustments.
    let error;
    if (editingDeductionId) {
      ({ error } = await supabase.from('driver_financial_adjustments').update(payload).eq('id', editingDeductionId));
    } else {
      ({ error } = await supabase.from('driver_financial_adjustments').insert(payload));
    }

    if (error) {
      console.error('Error saving deduction:', error);
      toast({ title: 'Error', description: 'Failed to save deduction: ' + error.message, variant: 'destructive' });
      return;
    }

    toast({ title: editingDeductionId ? 'Deduction updated' : 'Deduction added', description: 'This will be reflected in the driver\u2019s next summary.' });
    resetDeductionForm();
    await loadDeductions(driver.driver_name);
  };


  const toggleDeductionActive = async (item: DriverFinancialAdjustment) => {
    const { error } = await supabase
      .from('driver_financial_adjustments')
      .update({ active: !item.active })
      .eq('id', item.id);

    if (error) {
      toast({ title: 'Error', description: 'Failed to update deduction: ' + error.message, variant: 'destructive' });
      return;
    }
    await loadDeductions(item.driver_name);
  };

  const deleteDeduction = async (item: DriverFinancialAdjustment) => {
    if (!window.confirm(
      `Delete "${shortDescription(item.description) || item.category}"?\n\nIts payment history stays in the Deductions Report. If you only want to stop it being collected, use "Cancel charge" there (or untick Active) instead.`
    )) return;
    const { error } = await supabase.from('driver_financial_adjustments').delete().eq('id', item.id);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete deduction: ' + error.message, variant: 'destructive' });
      return;
    }
    await loadDeductions(item.driver_name);
  };

  const addRelationship = async () => {
    if (!driver) return;
    const tenantId = editData.tenant_id || driver.tenant_id;
    if (!tenantId || !newRelationship.child_driver_name) {
      toast({ title: 'Link driver needed', description: 'Please choose a driver to link.', variant: 'destructive' });
      return;
    }

    const { error } = await supabase.from('driver_relationships').insert({
      tenant_id: tenantId,
      parent_driver_name: driver.driver_name,
      child_driver_name: newRelationship.child_driver_name,
      relationship_type: newRelationship.relationship_type || 'linked',
      description: newRelationship.description || null,
      can_view_roster: newRelationship.can_view_roster ?? true,
      can_edit_roster: newRelationship.can_edit_roster ?? false,
      can_view_payroll: newRelationship.can_view_payroll ?? false,
      can_edit_payroll: newRelationship.can_edit_payroll ?? false,
      can_view_driver_details: newRelationship.can_view_driver_details ?? true,
      can_edit_driver_details: newRelationship.can_edit_driver_details ?? false,
      is_active: newRelationship.is_active ?? true,
    });

    if (error) {
      console.error('Error adding driver relationship:', error);
      toast({ title: 'Error', description: 'Failed to link driver: ' + error.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Driver linked', description: 'Driver relationship has been added.' });
    setShowRelationshipForm(false);
    setNewRelationship({ relationship_type: 'linked', can_view_roster: true, can_edit_roster: false, can_view_payroll: false, can_edit_payroll: false, can_view_driver_details: true, can_edit_driver_details: false, is_active: true });
    await loadRelationships(driver);
  };

  const deleteRelationship = async (relationshipId: string) => {
    if (!driver) return;
    const { error } = await supabase.from('driver_relationships').delete().eq('id', relationshipId);
    if (error) {
      toast({ title: 'Error', description: 'Failed to remove link: ' + error.message, variant: 'destructive' });
      return;
    }
    await loadRelationships(driver);
  };

  const saveEdit = async () => {
    if (!driver) return;

    // is_active drives the platform base charge's billable unit count
    // (see billableUnits.ts) -- warn before a real toggle, not on every
    // save (most saves don't touch it at all).
    const nextActive = editData.is_active ?? true;
    if (nextActive !== driver.is_active) {
      const proceed = await checkBillableUnitsChange('driver', nextActive);
      if (!proceed) return;
    }

    setSaving(true);

    try {
      if (dlFile) {
        await uploadDriverDoc('dl');
      }
      if (idFile) {
        await uploadDriverDoc('id');
      }

      const originalDl = formatDate(driver.dl_expiry || null);
      const originalId = formatDate(driver.id_expiry || null);
      const newDl = formatDate(editData.dl_expiry || null);
      const newId = formatDate(editData.id_expiry || null);

      const dlChanged = originalDl !== newDl;
      const idChanged = originalId !== newId;

      const payload: any = {
        driver_firstname: editData.driver_firstname || null,
        driver_lastname: editData.driver_lastname || null,
        ridewise_id: editData.ridewise_id || null,
        email: editData.email || null,
        phone: editData.phone || null,
        gender: (editData.gender as DriverGender) || null,
        dob: formatDate(editData.dob || null) || null,
        bank_account_name: editData.bank_account_name || null,
        bank_account_number: editData.bank_account_number || null,
        payout_reference_override: editData.payout_reference_override || null,
        insurance_start_date: formatDate(editData.insurance_start_date || null) || null,
        insurance_last_claim_date: formatDate(editData.insurance_last_claim_date || null) || null,
        insurance_claim_free: editData.insurance_claim_free ?? true,
        payment_type: (editData.payment_type as PaymentType) || null,
        last_vehicle_inspection_at: fromDateTimeLocal(String(editData.last_vehicle_inspection_at || '')),
        toro: editData.toro ?? null,
        lcp_units: editData.lcp_units ?? null,
        can_drive_manual: editData.can_drive_manual !== undefined ? editData.can_drive_manual : (driver.can_drive_manual ?? null),
        Password: editData.Password || null,
        AdminRights: editData.AdminRights || 'No',
        include_in_reports: editData.include_in_reports ?? true,
        is_active: editData.is_active ?? true,
        can_login: editData.can_login ?? driver.can_login ?? true,
        exempt_from_inspection: editData.exempt_from_inspection ?? false,
        updated_at: new Date().toISOString(),
        dl_number: editData.dl_number || null,
        dl_version:
          typeof editData.dl_version === 'number' ? editData.dl_version : editData.dl_version ? Number(editData.dl_version) : null,
        dl_taxicaller_updated: editData.dl_taxicaller_updated ?? (driver.dl_taxicaller_updated ?? false),
        id_taxicaller_updated: editData.id_taxicaller_updated ?? (driver.id_taxicaller_updated ?? false),
        dl_expiry: newDl || null,
        id_expiry: newId || null,
        dl_class: editData.dl_class || null,
        dl_issue_date: editData.dl_issue_date || null,
        dl_conditions: editData.dl_conditions || null,
        convictions_last_7_years: editData.convictions_last_7_years || null,
        accidents_claims_last_5_years: editData.accidents_claims_last_5_years || null,
        address: editData.address || null,
        driver_type_id: editData.driver_type_id || null,
        commission_percent_override:
          editData.commission_percent_override === null || editData.commission_percent_override === undefined || String(editData.commission_percent_override) === ''
            ? null
            : Number(editData.commission_percent_override),
        tax_treatment: (editData.tax_treatment as TaxTreatment) || 'PAYE',
        gst_registered: editData.gst_registered ?? false,
        gst_number: editData.gst_number || null,
        gst_registered_name: editData.gst_registered_name || null,
        gst_verified: editData.gst_verified ?? false,
        gst_verified_at: formatDate(editData.gst_verified_at || null) || null,
        tax_notes: editData.tax_notes || null,
        // Roster profile fields
        roster_positions: editData.roster_positions || [],
        roster_groups: editData.roster_groups || [],
        driver_tags: editData.driver_tags || [],
        roster_max_hours_per_week: editData.roster_max_hours_per_week ?? null,
        roster_preferred_vehicle: editData.roster_preferred_vehicle || null,
        roster_notes: editData.roster_notes || null,
      };

      if (dlChanged) {
        payload.dl_taxicaller_updated = false;
        payload.dl_scan_on_file = false;
      }
      if (idChanged) {
        payload.id_taxicaller_updated = false;
        payload.id_scan_on_file = false;
      }

      const { error } = await supabase.from('drivers').update(payload).eq('driver_name', driver.driver_name);

      if (error) {
        console.error('Error updating driver:', error);
        toast({ title: 'Error', description: 'Failed to update driver: ' + error.message, variant: 'destructive' });
        return;
      }

      // Push DL number / DL expiry / ID expiry straight back to TaxiCaller.
      // DMS is authoritative for these now -- this failing shouldn't block
      // the DMS save (the data is already safely saved above), so it's
      // reported as a separate, non-blocking toast rather than thrown.
      supabase.functions
        .invoke('push-driver-to-taxicaller', { body: { driver_name: driver.driver_name } })
        .then(({ data, error: pushErr }) => {
          if (pushErr || data?.ok === false) {
            toast({
              title: 'TaxiCaller sync failed',
              description: pushErr?.message ?? data?.error ?? 'Could not push driver changes to TaxiCaller.',
              variant: 'destructive',
            });
          }
        });

      await saveModulePermissions();

      // Persist per-payment-type commission % overrides for this driver
      try {
        await paymentPercentRef.current?.save();
      } catch (pctErr: any) {
        console.error('Error saving payment-type percentages:', pctErr);
        toast({ title: 'Warning', description: 'Driver saved, but payment-type % rates could not be saved: ' + (pctErr?.message || 'Unknown error'), variant: 'destructive' });
      }

      toast({ title: 'Success', description: 'Driver updated successfully' });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setEditData({});
    setDlFile(null);
    setIdFile(null);
    onClose();
  };

  if (!driver) return null;

  const dlUrgency = urgencyForDays(driver.dl_days_left);
  const idUrgency = urgencyForDays(driver.id_days_left);
  const dobForAge = formatDate((editData.dob ?? driver.dob) || null);
  const age = calcAge(dobForAge);

  // Resolve the current transmission value for the dropdown
  const currentTransmission = editData.can_drive_manual !== undefined
    ? editData.can_drive_manual
    : driver.can_drive_manual;

  const currentPositions = editData.roster_positions || [];
  const currentGroups = editData.roster_groups || [];
  const currentTags = editData.driver_tags || [];
  const selectedDriverType = driverTypes.find((type) => type.id === (editData.driver_type_id ?? driver.driver_type_id));
  const effectiveCommissionPercent = editData.commission_percent_override ?? selectedDriverType?.default_commission_percent ?? null;
  const companyLabel = tenantInfo?.tenant_name || 'Current company';
  const companyCodeLabel = tenantInfo?.company_login_code ? ` (${tenantInfo.company_login_code})` : '';

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
        <DialogHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10 pb-4 border-b border-gray-700">
          <DialogTitle className="text-lg font-semibold text-yellow-500">
            Edit Driver: {driver.driver_name}
          </DialogTitle>
          <div className="flex gap-2">
            <Button onClick={saveEdit} disabled={saving} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </DialogHeader>

        <FolderTabNav
          className="pt-4"
          tabs={[
            { value: 'details', label: 'Details', icon: User },
            { value: 'admin', label: 'Admin', icon: ShieldCheck },
            { value: 'compliance', label: 'IDs', icon: ClipboardCheck },
            { value: 'payments', label: 'Payments', icon: CreditCard },
            { value: 'deductions', label: 'Deductions', icon: MinusCircle },
            { value: 'roster', label: 'Roster', icon: Calendar },
            { value: 'relationships', label: 'Links', icon: Link2 },
          ]}
          value={activeTab}
          onValueChange={setActiveTab}
        />
        <div className="rounded-b-xl rounded-tr-xl border border-yellow-900/20 bg-zinc-900 p-4 sm:p-5">
          {activeTab === 'details' && (
          <div>
          <Section title="Driver Details" subtitle="Core driver info and contact details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-yellow-500">Driver Name (Primary Key)</Label>
                <Input value={driver.driver_name} disabled className="bg-gray-700 text-gray-400" />
              </div>
              <div>
                <Label>RideWise ID</Label>
                <Input value={editData.ridewise_id || ''} onChange={(e) => setEditData({ ...editData, ridewise_id: e.target.value })} />
              </div>
              <div>
                <Label>First Name</Label>
                <Input
                  value={editData.driver_firstname || ''}
                  onChange={(e) => setEditData({ ...editData, driver_firstname: e.target.value })}
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input value={editData.driver_lastname || ''} onChange={(e) => setEditData({ ...editData, driver_lastname: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editData.email || ''} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input type="tel" value={editData.phone || ''} onChange={(e) => setEditData({ ...editData, phone: normalizePhoneInput(e.target.value) })} />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Input
                  value={editData.address ?? driver.address ?? ''}
                  onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                  placeholder="Street, suburb, city"
                />
              </div>
              <div>
                <Label>Gender</Label>
                <Select
                  value={toSelectValue(editData.gender ?? driver.gender)}
                  onValueChange={(value) => setEditData({ ...editData, gender: fromSelectValue(value) as DriverGender | null })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>DOB</Label>
                <Input
                  type="date"
                  value={formatDate((editData.dob ?? driver.dob) || null)}
                  onChange={(e) => setEditData({ ...editData, dob: e.target.value })}
                />
              </div>
              <div>
                <Label>Age</Label>
                <Input value={formatAgeLabel(age)} disabled className="bg-gray-700 text-gray-300" />
              </div>
            </div>
          </Section>

          <Section title="Profile Tags" subtitle="Driver attributes and qualifications">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {DRIVER_TAG_OPTIONS.map((tag) => (
                <div key={tag} className="flex items-center gap-2">
                  <Checkbox
                    id={`tag-${tag}`}
                    checked={currentTags.includes(tag)}
                    onCheckedChange={() => toggleTag(tag)}
                  />
                  <Label htmlFor={`tag-${tag}`} className="text-sm cursor-pointer">{tag}</Label>
                </div>
              ))}
            </div>
          </Section>
          </div>
          )}
          {activeTab === 'admin' && (
          <div>
          <Section title="Admin" subtitle="Access, visibility, and company login context">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Company</Label>
                <Input value={tenantInfo?.company_login_code || tenantInfo?.tenant_name || 'Current company'} disabled className="bg-gray-700 text-gray-300" />
                <p className="text-xs text-gray-500 mt-1">Company is inherited from the logged-in company and is not changed here.</p>
              </div>
              <div>
                <Label className="text-yellow-500">Password *</Label>
                <Input type="password" value={editData.Password || ''} onChange={(e) => setEditData({ ...editData, Password: e.target.value })} />
              </div>
              <div>
                <Label>Admin Rights</Label>
                <Select value={editData.AdminRights || 'No'} onValueChange={(value) => setEditData({ ...editData, AdminRights: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox
                  id={`edit-is-active-${driver.driver_name}`}
                  checked={editData.is_active ?? true}
                  onCheckedChange={(checked) => setEditData({ ...editData, is_active: checked as boolean })}
                />
                <Label htmlFor={`edit-is-active-${driver.driver_name}`}>Active</Label>
              </div>
              <div className="pt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-can-login-${driver.driver_name}`}
                    checked={editData.can_login ?? driver.can_login ?? true}
                    onCheckedChange={(checked) => setEditData({ ...editData, can_login: checked as boolean })}
                  />
                  <Label htmlFor={`edit-can-login-${driver.driver_name}`}>Can log in</Label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Untick to block this person from logging in (and sign out any phone they're already on) while keeping
                  them active for payments, reports and rosters.
                </p>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id={`edit-include-in-reports-${driver.driver_name}`}
                  checked={editData.include_in_reports ?? true}
                  onCheckedChange={(checked) => setEditData({ ...editData, include_in_reports: checked as boolean })}
                />
                <Label htmlFor={`edit-include-in-reports-${driver.driver_name}`}>Include in Reports</Label>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id={`edit-exempt-from-inspection-${driver.driver_name}`}
                  checked={editData.exempt_from_inspection ?? false}
                  onCheckedChange={(checked) => setEditData({ ...editData, exempt_from_inspection: checked as boolean })}
                />
                <Label htmlFor={`edit-exempt-from-inspection-${driver.driver_name}`}>Exempt from Vehicle Inspection</Label>
              </div>
            </div>

            {(editData.AdminRights || driver.AdminRights || 'No') === 'Yes' && (
              <div className="mt-5 rounded-lg border border-gray-700 bg-gray-950/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-sm font-semibold text-yellow-500">Module Permissions</div>
                    <div className="text-xs text-gray-400">Tick what this admin login can see and edit. This saves to the SaaS permission override layer.</div>
                  </div>
                </div>
                <div className="overflow-x-auto rounded border border-gray-800">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800/80 text-gray-300">
                      <tr>
                        <th className="text-left px-3 py-2">Module</th>
                        <th className="text-center px-3 py-2 w-28">Can See</th>
                        <th className="text-center px-3 py-2 w-28">Can Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appModules.map((module) => {
                        const perm = modulePermissions[module.module_key] || { module_key: module.module_key, can_view: false, can_edit: false };
                        return (
                          <tr key={module.module_key} className="border-t border-gray-800">
                            <td className="px-3 py-2 text-gray-200">{module.module_name}</td>
                            <td className="px-3 py-2 text-center">
                              <Checkbox
                                checked={!!perm.can_view}
                                onCheckedChange={(checked) => toggleModulePermission(module.module_key, 'can_view', checked as boolean)}
                              />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Checkbox
                                checked={!!perm.can_edit}
                                onCheckedChange={(checked) => toggleModulePermission(module.module_key, 'can_edit', checked as boolean)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Section>
          </div>
          )}
          {activeTab === 'compliance' && (
          <div>
          <Section title="IDs & Compliance" subtitle="Expiry dates and compliance tracking">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>DL Number</Label>
                <Input value={editData.dl_number ?? driver.dl_number ?? ''} onChange={(e) => setEditData({ ...editData, dl_number: e.target.value })} />
              </div>
              <div>
                <Label>DL Version</Label>
                <Input
                  type="number"
                  value={editData.dl_version ?? (driver.dl_version ?? '')}
                  onChange={(e) => setEditData({ ...editData, dl_version: e.target.value === '' ? null : Number(e.target.value) })}
                  placeholder="e.g. 673"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  DL Expiry
                  <span className={dlUrgency === 'red' ? 'text-red-400' : dlUrgency === 'orange' ? 'text-orange-400' : 'text-gray-400'}>
                    ({formatDaysLeft(driver.dl_days_left)})
                  </span>
                </Label>
                <Input
                  type="date"
                  value={formatDate(editData.dl_expiry ?? driver.dl_expiry ?? null)}
                  onChange={(e) => setEditData({ ...editData, dl_expiry: e.target.value })}
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openScan(editData.dl_scan_path ?? driver.dl_scan_path ?? null)}
                    disabled={!(editData.dl_scan_path ?? driver.dl_scan_path)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Open Scan
                  </Button>
                  <Input type="file" accept="image/*,application/pdf" onChange={(e) => setDlFile(e.target.files?.[0] || null)} className="max-w-[180px]" />
                  <Button type="button" size="sm" onClick={() => uploadDriverDoc('dl')} disabled={!dlFile || uploading.dl}>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading.dl ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`dl-scan-on-file-${driver.driver_name}`}
                      checked={(editData.dl_scan_on_file ?? driver.dl_scan_on_file ?? false) as boolean}
                      onCheckedChange={(checked) => setEditData({ ...editData, dl_scan_on_file: checked as boolean })}
                    />
                    <Label htmlFor={`dl-scan-on-file-${driver.driver_name}`} className="text-sm">
                      Scan on file
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`dl-taxicaller-updated-${driver.driver_name}`}
                      checked={(editData.dl_taxicaller_updated ?? driver.dl_taxicaller_updated ?? false) as boolean}
                      onCheckedChange={(checked) => setEditData({ ...editData, dl_taxicaller_updated: checked as boolean })}
                    />
                    <Label htmlFor={`dl-taxicaller-updated-${driver.driver_name}`} className="text-sm">
                      TaxiCaller updated
                    </Label>
                  </div>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  ID Expiry
                  <span className={idUrgency === 'red' ? 'text-red-400' : idUrgency === 'orange' ? 'text-orange-400' : 'text-gray-400'}>
                    ({formatDaysLeft(driver.id_days_left)})
                  </span>
                </Label>
                <Input
                  type="date"
                  value={formatDate(editData.id_expiry ?? driver.id_expiry ?? null)}
                  onChange={(e) => setEditData({ ...editData, id_expiry: e.target.value })}
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openScan(editData.id_scan_path ?? driver.id_scan_path ?? null)}
                    disabled={!(editData.id_scan_path ?? driver.id_scan_path)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Open Scan
                  </Button>
                  <Input type="file" accept="image/*,application/pdf" onChange={(e) => setIdFile(e.target.files?.[0] || null)} className="max-w-[180px]" />
                  <Button type="button" size="sm" onClick={() => uploadDriverDoc('id')} disabled={!idFile || uploading.id}>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading.id ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`id-scan-on-file-${driver.driver_name}`}
                      checked={(editData.id_scan_on_file ?? driver.id_scan_on_file ?? false) as boolean}
                      onCheckedChange={(checked) => setEditData({ ...editData, id_scan_on_file: checked as boolean })}
                    />
                    <Label htmlFor={`id-scan-on-file-${driver.driver_name}`} className="text-sm">
                      Scan on file
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`id-taxicaller-updated-${driver.driver_name}`}
                      checked={(editData.id_taxicaller_updated ?? driver.id_taxicaller_updated ?? false) as boolean}
                      onCheckedChange={(checked) => setEditData({ ...editData, id_taxicaller_updated: checked as boolean })}
                    />
                    <Label htmlFor={`id-taxicaller-updated-${driver.driver_name}`} className="text-sm">
                      TaxiCaller updated
                    </Label>
                  </div>
                </div>
              </div>
              <div>
                <Label>Last Vehicle Inspection (date/time)</Label>
                <Input
                  type="datetime-local"
                  value={toDateTimeLocal(editData.last_vehicle_inspection_at ?? driver.last_vehicle_inspection_at ?? null)}
                  onChange={(e) => setEditData({ ...editData, last_vehicle_inspection_at: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-6 pt-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-toro-${driver.driver_name}`}
                    checked={(editData.toro ?? driver.toro ?? false) as boolean}
                    onCheckedChange={(checked) => setEditData({ ...editData, toro: checked as boolean })}
                  />
                  <Label htmlFor={`edit-toro-${driver.driver_name}`}>TORO</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-lcp-${driver.driver_name}`}
                    checked={(editData.lcp_units ?? driver.lcp_units ?? false) as boolean}
                    onCheckedChange={(checked) => setEditData({ ...editData, lcp_units: checked as boolean })}
                  />
                  <Label htmlFor={`edit-lcp-${driver.driver_name}`}>LCP Units</Label>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Insurance/Licence Details" subtitle="Extra licence detail an insurance claim form asks for -- separate from the DL number/expiry above">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Licence Class</Label>
                <Input
                  value={editData.dl_class ?? driver.dl_class ?? ''}
                  onChange={(e) => setEditData({ ...editData, dl_class: e.target.value })}
                  placeholder="e.g. 1 - Car Licence"
                />
              </div>
              <div>
                <Label>Licence Issue Date</Label>
                <Input
                  type="date"
                  value={formatDate(editData.dl_issue_date ?? driver.dl_issue_date ?? null)}
                  onChange={(e) => setEditData({ ...editData, dl_issue_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Licence Conditions (if any)</Label>
                <Input
                  value={editData.dl_conditions ?? driver.dl_conditions ?? ''}
                  onChange={(e) => setEditData({ ...editData, dl_conditions: e.target.value })}
                  placeholder="e.g. Corrective Lenses -- leave blank if none"
                />
              </div>
              <div>
                <Label>Convictions in Last 7 Years (excl. parking)</Label>
                <Input
                  value={editData.convictions_last_7_years ?? driver.convictions_last_7_years ?? ''}
                  onChange={(e) => setEditData({ ...editData, convictions_last_7_years: e.target.value })}
                  placeholder="Leave blank if none"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Accidents/Claims (Any Vehicle) in Last 5 Years</Label>
                <Input
                  value={editData.accidents_claims_last_5_years ?? driver.accidents_claims_last_5_years ?? ''}
                  onChange={(e) => setEditData({ ...editData, accidents_claims_last_5_years: e.target.value })}
                  placeholder="Leave blank if none"
                />
              </div>
            </div>
          </Section>
          </div>
          )}
          {activeTab === 'payments' && (
          <div>
          <Section title="Payments" subtitle="Driver type, commission, GST/tax details and payout banking">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Driver Type</Label>
                <Select
                  value={toSelectValue(editData.driver_type_id ?? driver.driver_type_id)}
                  onValueChange={(value) => setEditData({ ...editData, driver_type_id: fromSelectValue(value) })}
                  disabled={payHistoryCount > 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SELECTION}>— Select —</SelectItem>
                    {driverTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.type_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedDriverType && (
                  <p className="text-xs text-gray-500 mt-1">
                    {payHistoryCount > 0
                      ? 'Managed by the pay arrangement history below.'
                      : selectedDriverType.uses_payment_type_percentages ? 'Uses payment-type payable % rules.' : 'Ignores payment-type payable % rules.'}
                  </p>
                )}
              </div>
              <DriverPayArrangements
                driverName={driver.driver_name}
                driverTypes={driverTypes}
                onCount={setPayHistoryCount}
                onCurrentTypeChanged={(id) => setEditData((prev: any) => ({ ...prev, driver_type_id: id }))}
              />
              <div>
                <Label>Tax Treatment</Label>
                <Select
                  value={(editData.tax_treatment ?? driver.tax_treatment ?? 'PAYE') as TaxTreatment}
                  onValueChange={(value) => setEditData({
                    ...editData,
                    tax_treatment: value as TaxTreatment,
                    gst_registered: value === 'GST_REGISTERED' ? true : editData.gst_registered,
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tax treatment" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_TREATMENT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Driver Type Default Commission %</Label>
                <Input value={selectedDriverType?.default_commission_percent ?? ''} disabled className="bg-gray-700 text-gray-300" />
              </div>
              <div>
                <Label>Commission Override %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={editData.commission_percent_override ?? ''}
                  onChange={(e) => setEditData({
                    ...editData,
                    commission_percent_override: e.target.value === '' ? null : Number(e.target.value),
                  })}
                  placeholder="Leave blank to use driver type"
                />
              </div>
              <div>
                <Label>Effective Commission %</Label>
                <Input value={effectiveCommissionPercent ?? 'N/A'} disabled className="bg-gray-700 text-gray-300" />
                {selectedDriverType?.is_leased && (
                  <p className="text-xs text-gray-500 mt-1">Lease payments should be set up under Deductions as a recurring lease deduction.</p>
                )}
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox
                  id={`edit-gst-registered-${driver.driver_name}`}
                  checked={editData.gst_registered ?? driver.gst_registered ?? false}
                  onCheckedChange={(checked) => setEditData({
                    ...editData,
                    gst_registered: checked as boolean,
                    tax_treatment: checked ? 'GST_REGISTERED' : (editData.tax_treatment ?? driver.tax_treatment ?? 'PAYE'),
                  })}
                />
                <Label htmlFor={`edit-gst-registered-${driver.driver_name}`}>GST Registered</Label>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox
                  id={`edit-gst-verified-${driver.driver_name}`}
                  checked={editData.gst_verified ?? driver.gst_verified ?? false}
                  onCheckedChange={(checked) => setEditData({
                    ...editData,
                    gst_verified: checked as boolean,
                    gst_verified_at: checked ? (editData.gst_verified_at || new Date().toISOString().split('T')[0]) : editData.gst_verified_at,
                  })}
                />
                <Label htmlFor={`edit-gst-verified-${driver.driver_name}`}>GST Verified</Label>
              </div>
              <div>
                <Label>GST Number</Label>
                <Input value={editData.gst_number || ''} onChange={(e) => setEditData({ ...editData, gst_number: e.target.value })} placeholder="e.g. 123-456-789" />
              </div>
              <div>
                <Label>GST Registered Name</Label>
                <Input value={editData.gst_registered_name || ''} onChange={(e) => setEditData({ ...editData, gst_registered_name: e.target.value })} />
              </div>
              <div>
                <Label>GST Verified Date</Label>
                <Input
                  type="date"
                  value={formatDate(editData.gst_verified_at ?? driver.gst_verified_at ?? null)}
                  onChange={(e) => setEditData({ ...editData, gst_verified_at: e.target.value })}
                />
              </div>
              <div>
                <Label>Bank Account Name</Label>
                <Input value={editData.bank_account_name || ''} onChange={(e) => setEditData({ ...editData, bank_account_name: e.target.value })} />
              </div>
              <div>
                <Label>Bank Account Number</Label>
                <Input value={editData.bank_account_number || ''} onChange={(e) => setEditData({ ...editData, bank_account_number: e.target.value })} />
              </div>
              <div>
                <Label>Payout Reference Override</Label>
                <Input
                  value={editData.payout_reference_override || ''}
                  onChange={(e) => setEditData({ ...editData, payout_reference_override: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Tax Notes</Label>
                <Input value={editData.tax_notes || ''} onChange={(e) => setEditData({ ...editData, tax_notes: e.target.value })} placeholder="Any GST/tax notes for payroll" />
              </div>
            </div>

            <div className="mt-5">
              <DriverPaymentTypePercentages
                ref={paymentPercentRef}
                driverName={driver.driver_name}
                tenantId={editData.tenant_id ?? driver.tenant_id ?? null}
                effectiveCommissionPercent={
                  typeof effectiveCommissionPercent === 'number'
                    ? effectiveCommissionPercent
                    : effectiveCommissionPercent != null
                    ? Number(effectiveCommissionPercent)
                    : null
                }
                enabled={!!selectedDriverType?.uses_payment_type_percentages}
              />
            </div>
          </Section>
          </div>
          )}
          {activeTab === 'deductions' && (
          <div>
          <Section title="Deductions" subtitle="Insurance, recurring deductions, reducing balances, one-off deductions and credits">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Insurance Start Date</Label>
                <Input
                  type="date"
                  value={formatDate(editData.insurance_start_date ?? driver.insurance_start_date ?? null)}
                  onChange={(e) => setEditData({ ...editData, insurance_start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Insurance Last Claim Date</Label>
                <Input
                  type="date"
                  value={formatDate(editData.insurance_last_claim_date ?? driver.insurance_last_claim_date ?? null)}
                  onChange={(e) => setEditData({ ...editData, insurance_last_claim_date: e.target.value })}
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox
                  id={`edit-insurance-claim-free-${driver.driver_name}`}
                  checked={editData.insurance_claim_free ?? true}
                  onCheckedChange={(checked) => setEditData({ ...editData, insurance_claim_free: checked as boolean })}
                />
                <Label htmlFor={`edit-insurance-claim-free-${driver.driver_name}`}>Insurance Claim Free</Label>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-yellow-500">Deductions &amp; Extra Payments</div>
                <div className="text-xs text-gray-400">
                  Add recurring, reducing-balance or one-off deductions -- or extra payments to this driver
                  (sim card reimbursement, correcting an overpaid deduction, a bonus, etc.).
                </div>
              </div>
              <Button type="button" size="sm" onClick={() => { if (showDeductionForm) { resetDeductionForm(); } else { resetDeductionForm(); setShowDeductionForm(true); } }}>
                <Plus className="w-4 h-4 mr-2" /> Add Deduction / Payment
              </Button>

            </div>

            {showDeductionForm && (
              <div className="mt-4 rounded-lg border border-gray-700 bg-gray-950/40 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Type</Label>
                    <Select
                      value={newDeduction.adjustment_type || 'deduction'}
                      onValueChange={(value) => {
                        const isCredit = value === 'credit';
                        const defaultCategory = isCredit
                          ? (paymentCategories[0]?.category_key || 'reimbursement')
                          : (deductionCategories[0]?.category_key || 'manual');
                        setNewDeduction({
                          ...newDeduction,
                          adjustment_type: value as any,
                          category: defaultCategory,
                          requires_bank_account: false,
                          bank_account_number: null,
                          bank_account_name: null,
                          payment_reference: null,
                        });
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deduction">Deduction</SelectItem>
                        <SelectItem value="credit">Extra Payment (Credit)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{newDeduction.adjustment_type === 'credit' ? 'Reason' : 'Category'}</Label>
                    {newDeduction.adjustment_type === 'credit' ? (
                      <Select value={newDeduction.category || 'reimbursement'} onValueChange={(value) => setNewDeduction({ ...newDeduction, category: value as any })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {paymentCategories.length === 0 ? (
                            <>
                              <SelectItem value="sim_card">Sim Card Reimbursement</SelectItem>
                              <SelectItem value="overpayment_correction">Deduction Overpayment Correction</SelectItem>
                              <SelectItem value="bonus">Bonus</SelectItem>
                              <SelectItem value="reimbursement">General Reimbursement</SelectItem>
                            </>
                          ) : (
                            paymentCategories.map((c) => (
                              <SelectItem key={c.id} value={c.category_key}>{c.label}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    ) : (
                      <>
                        <Select
                          value={newDeduction.category || 'manual'}
                          onValueChange={(value) => {
                            // Default the checkbox from the category's usual
                            // shape (individual-payout categories like
                            // Structured Repayment usually need one) -- still
                            // just a starting point, not a lock: the admin
                            // can tick/untick it per deduction below.
                            const nextCat = deductionCategories.find((c) => c.category_key === value);
                            setNewDeduction({
                              ...newDeduction,
                              category: value as any,
                              requires_bank_account: nextCat?.payout_mode === 'individual',
                            });
                          }}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {deductionCategories.length === 0 ? (
                              <>
                                <SelectItem value="insurance">Insurance</SelectItem>
                                <SelectItem value="structured_repayment">Structured Repayment</SelectItem>
                                <SelectItem value="manual">Manual</SelectItem>
                                <SelectItem value="insurance_rebate">Insurance Rebate</SelectItem>
                              </>
                            ) : (
                              deductionCategories.map((c) => (
                                <SelectItem key={c.id} value={c.category_key}>{c.label}</SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        {(() => {
                          const selectedCat = deductionCategories.find(
                            (c) => c.category_key === (newDeduction.category || 'manual')
                          );
                          if (selectedCat?.payout_mode === 'company_wide' && selectedCat.bank_account_number) {
                            return (
                              <p className="mt-1 text-[11px] text-yellow-500">
                                Pays to {selectedCat.bank_account_name || selectedCat.label}: {selectedCat.bank_account_number}
                              </p>
                            );
                          }
                          return null;
                        })()}
                      </>
                    )}
                  </div>
                  <div>
                    <Label>Mode</Label>
                    <Select value={newDeduction.adjustment_mode || 'one_off'} onValueChange={(value) => setNewDeduction({ ...newDeduction, adjustment_mode: value as any })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="one_off">One Off</SelectItem>
                        <SelectItem value="recurring">Recurring</SelectItem>
                        <SelectItem value="reducing_balance">Reducing Balance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount</Label>
                    <Input type="number" step="0.01" value={newDeduction.amount ?? ''} onChange={(e) => setNewDeduction({ ...newDeduction, amount: e.target.value === '' ? 0 : Number(e.target.value) })} />
                  </div>
                  <div>
                    <Label>Total Owing</Label>
                    <Input type="number" step="0.01" value={newDeduction.total_amount ?? ''} onChange={(e) => setNewDeduction({ ...newDeduction, total_amount: e.target.value === '' ? null : Number(e.target.value), remaining_balance: e.target.value === '' ? null : Number(e.target.value) })} placeholder="For reducing balance" />
                  </div>
                  <div>
                    <Label>Per Payout</Label>
                    <Input type="number" step="0.01" value={newDeduction.apply_per_payout ?? ''} onChange={(e) => setNewDeduction({ ...newDeduction, apply_per_payout: e.target.value === '' ? null : Number(e.target.value) })} placeholder="e.g. 10" />
                  </div>
                  <div>
                    <Label>Minimum Net Pay</Label>
                    <Input type="number" step="0.01" value={newDeduction.minimum_net_pay ?? ''} onChange={(e) => setNewDeduction({ ...newDeduction, minimum_net_pay: e.target.value === '' ? null : Number(e.target.value) })} placeholder="Optional" />
                  </div>
                  <div>
                    <Label>Starts On</Label>
                    <Input type="date" value={formatDate(newDeduction.starts_on || null)} onChange={(e) => setNewDeduction({ ...newDeduction, starts_on: e.target.value })} />
                  </div>
                  <div>
                    <Label>Ends On</Label>
                    <Input type="date" value={formatDate(newDeduction.ends_on || null)} onChange={(e) => setNewDeduction({ ...newDeduction, ends_on: e.target.value })} />
                  </div>
                  {newDeduction.adjustment_mode === 'recurring' && (
                    <div className="flex items-center gap-2 md:col-span-3">
                      <Checkbox
                        checked={!!newDeduction.prorate_partial_weeks}
                        onCheckedChange={(checked) => setNewDeduction({ ...newDeduction, prorate_partial_weeks: checked as boolean })}
                      />
                      <Label>Pro-rate part weeks (when Starts On / Ends On falls mid-week, take only that share of the weekly amount — e.g. a lease starting Thursday takes 4/7 of the first week)</Label>
                    </div>
                  )}
                  {newDeduction.adjustment_type !== 'credit' &&
                    deductionCategories.find((c) => c.category_key === (newDeduction.category || 'manual'))?.payout_mode !== 'company_wide' && (
                      <div className="flex items-center gap-2 md:col-span-3">
                        <Checkbox
                          checked={!!newDeduction.requires_bank_account}
                          onCheckedChange={(checked) => setNewDeduction({ ...newDeduction, requires_bank_account: checked as boolean })}
                        />
                        <Label>Bank account required (pays to a 3rd party -- leave unticked if it just repays the company)</Label>
                      </div>
                    )}
                  {newDeduction.adjustment_type !== 'credit' && newDeduction.requires_bank_account && (
                    <div className="md:col-span-3 rounded-lg border border-blue-900/40 bg-blue-950/20 p-3">
                      <p className="mb-2 text-xs text-blue-300">
                        Enter the account this specific deduction pays to.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Bank Account Number</Label>
                          <Input
                            value={newDeduction.bank_account_number || ''}
                            onChange={(e) => setNewDeduction({ ...newDeduction, bank_account_number: e.target.value })}
                            placeholder="e.g. 12-3456-0789012-00"
                          />
                        </div>
                        <div>
                          <Label>Account Name</Label>
                          <Input
                            value={newDeduction.bank_account_name || ''}
                            onChange={(e) => setNewDeduction({ ...newDeduction, bank_account_name: e.target.value })}
                            placeholder="e.g. District Court Fines Unit"
                          />
                        </div>
                        <div>
                          <Label>Reference / Case Number</Label>
                          <Input
                            value={newDeduction.payment_reference || ''}
                            onChange={(e) => setNewDeduction({ ...newDeduction, payment_reference: e.target.value })}
                            placeholder="e.g. CR-2026-01234"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="md:col-span-3">
                    <Label>Description</Label>
                    <Input value={newDeduction.description || ''} onChange={(e) => setNewDeduction({ ...newDeduction, description: e.target.value })} placeholder="e.g. Uniform repayment, lease payment, excess repayment" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={newDeduction.auto_apply ?? true} onCheckedChange={(checked) => setNewDeduction({ ...newDeduction, auto_apply: checked as boolean })} />
                    <Label>Auto apply</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={newDeduction.active ?? true} onCheckedChange={(checked) => setNewDeduction({ ...newDeduction, active: checked as boolean })} />
                    <Label>Active</Label>
                  </div>
                  <div className="flex justify-end gap-2 md:col-span-3">
                    <Button type="button" variant="outline" onClick={resetDeductionForm}>Cancel</Button>
                    <Button type="button" onClick={addDeduction}>{editingDeductionId ? 'Save Changes' : 'Save Deduction'}</Button>
                  </div>

                </div>
              </div>
            )}

            <div className="mt-4 overflow-x-auto rounded border border-gray-800">
              <table className="w-full text-sm">
                <thead className="bg-gray-800/80 text-gray-300">
                  <tr>
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Mode</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-right px-3 py-2">Remaining</th>
                    <th className="text-center px-3 py-2">Active</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deductions.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-5 text-center text-gray-500">No deductions or credits set up for this driver yet.</td></tr>
                  ) : deductions.map((item) => (
                    <tr key={item.id} className="border-t border-gray-800">
                      <td className="px-3 py-2 text-gray-200">
                        <span className="inline-flex items-center gap-1.5">
                          <span title={item.description || item.category}>{shortDescription(item.description) || item.category}</span>
                          {item.deduction_status && item.deduction_status !== 'active' && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-gray-600 text-gray-400">
                              {item.deduction_status}
                            </span>
                          )}
                          {item.requires_bank_account && (
                            <CreditCard className="w-3.5 h-3.5 text-blue-400 shrink-0" aria-label="Pays to a 3rd party bank account" />
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 capitalize">{item.adjustment_type}</td>
                      <td className="px-3 py-2 capitalize">{item.adjustment_mode.replace('_', ' ')}</td>
                      <td className="px-3 py-2 text-right">${Number(item.amount || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">{item.remaining_balance === null || item.remaining_balance === undefined ? '-' : `$${Number(item.remaining_balance).toFixed(2)}`}</td>
                      <td className="px-3 py-2 text-center"><Checkbox checked={!!item.active} onCheckedChange={() => toggleDeductionActive(item)} /></td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" size="sm" onClick={() => startEditDeduction(item)} title="Edit">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => deleteDeduction(item)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          </div>
          )}
          {activeTab === 'roster' && (
          <div>
          <Section title="Roster Profile" subtitle="Roster positions, groups, and scheduling preferences">
            <div className="space-y-4">
              {/* Positions */}
              <div>
                <Label className="text-yellow-500 text-xs flex items-center gap-1.5 mb-2">
                  <Calendar className="w-3.5 h-3.5" />
                  Positions (Shift Types)
                </Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {currentPositions.map(code => {
                    const st = shiftTypes.find(s => s.code === code);
                    return (
                      <Badge
                        key={code}
                        className="cursor-pointer text-white text-xs px-2 py-1 flex items-center gap-1"
                        style={{ backgroundColor: st?.default_colour_hex || '#6366F1' }}
                        onClick={() => togglePosition(code)}
                      >
                        {st?.label || code}
                        <X className="w-3 h-3 ml-0.5" />
                      </Badge>
                    );
                  })}
                  {currentPositions.length === 0 && (
                    <span className="text-xs text-gray-500 italic">No positions assigned</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 p-2 rounded border border-gray-700 bg-gray-800/40 max-h-32 overflow-y-auto">
                  {shiftTypes.filter(st => !currentPositions.includes(st.code)).map(st => (
                    <button
                      key={st.code}
                      onClick={() => togglePosition(st.code)}
                      className="text-xs px-2 py-1 rounded border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 transition-colors flex items-center gap-1.5"
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: st.default_colour_hex || '#6366F1' }}
                      />
                      {st.label}
                    </button>
                  ))}
                  {shiftTypes.filter(st => !currentPositions.includes(st.code)).length === 0 && (
                    <span className="text-xs text-gray-500 italic">All positions assigned</span>
                  )}
                </div>
              </div>

              {/* Groups - loaded from DB */}
              <div>
                <Label className="text-yellow-500 text-xs flex items-center gap-1.5 mb-2">
                  <Users className="w-3.5 h-3.5" />
                  Groups
                </Label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {currentGroups.map(group => {
                    const dbg = dbGroups.find(g => g.name === group);
                    return (
                      <Badge
                        key={group}
                        className="cursor-pointer text-white text-xs px-2 py-1 flex items-center gap-1"
                        style={{ backgroundColor: dbg?.colour_hex || '#6366F1' }}
                        onClick={() => toggleGroup(group)}
                      >
                        {group}
                        <X className="w-3 h-3 ml-0.5" />
                      </Badge>
                    );
                  })}
                  {currentGroups.length === 0 && (
                    <span className="text-xs text-gray-500 italic">No groups assigned</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 p-2 rounded border border-gray-700 bg-gray-800/40">
                  {(dbGroups.length > 0 ? dbGroups.map(g => g.name) : ROSTER_GROUP_OPTIONS_FALLBACK).filter(g => !currentGroups.includes(g)).map(group => {
                    const dbg = dbGroups.find(g => g.name === group);
                    return (
                      <button
                        key={group}
                        onClick={() => toggleGroup(group)}
                        className="text-xs px-2 py-1 rounded border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 transition-colors flex items-center gap-1.5"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: dbg?.colour_hex || '#6366F1' }}
                        />
                        + {group}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Transmission, Max Hours, Preferred Vehicle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <Label className="text-yellow-500 text-xs">Transmission Ability</Label>
                  <Select
                    value={transmissionToSelectValue(currentTransmission)}
                    onValueChange={(value) => setEditData({ ...editData, can_drive_manual: transmissionFromSelectValue(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select transmission ability" />
                    </SelectTrigger>
                    <SelectContent>
                      {TRANSMISSION_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="flex items-center gap-2">
                            {opt.value === 'auto_manual' && <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />}
                            {opt.value === 'auto_only' && <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />}
                            {opt.value === NO_SELECTION && <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />}
                            {opt.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-yellow-500 text-xs">Max Hours / Week</Label>
                  <Input
                    type="number"
                    min="0"
                    max="168"
                    step="1"
                    value={editData.roster_max_hours_per_week ?? ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      roster_max_hours_per_week: e.target.value === '' ? null : parseFloat(e.target.value)
                    })}
                    placeholder="e.g. 40"
                  />
                </div>
                <div>
                  <Label className="text-yellow-500 text-xs">Preferred Vehicle</Label>
                  <Select
                    value={editData.roster_preferred_vehicle || NO_SELECTION}
                    onValueChange={(value) => setEditData({ ...editData, roster_preferred_vehicle: value === NO_SELECTION ? null : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_SELECTION}>— None —</SelectItem>
                      {vehicles.map(v => (
                        <SelectItem key={v.vehicle_number} value={v.vehicle_number}>
                          {v.vehicle_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-yellow-500 text-xs">Roster Notes</Label>
                  <Input
                    value={editData.roster_notes || ''}
                    onChange={(e) => setEditData({ ...editData, roster_notes: e.target.value })}
                    placeholder="e.g. Prefers morning shifts"
                  />
                </div>
              </div>
            </div>
          </Section>
          </div>
          )}
          {activeTab === 'relationships' && (
          <div className="space-y-4">
          <Section title="Linked Drivers" subtitle="Leaseholders, managed drivers and other driver-to-driver links">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs text-gray-400">Use this for Bill → Grant style links. External company access will use the selected-driver access layer later.</div>
              <Button type="button" size="sm" onClick={() => setShowRelationshipForm((v) => !v)}>
                <Plus className="w-4 h-4 mr-2" /> Link Driver
              </Button>
            </div>

            {showRelationshipForm && (
              <div className="mb-4 rounded-lg border border-gray-700 bg-gray-950/40 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Linked Driver</Label>
                    <Select value={newRelationship.child_driver_name || NO_SELECTION} onValueChange={(value) => setNewRelationship({ ...newRelationship, child_driver_name: value === NO_SELECTION ? undefined : value })}>
                      <SelectTrigger><SelectValue placeholder="Choose driver" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_SELECTION}>— Select —</SelectItem>
                        {allDriverNames.filter((d) => d.driver_name !== driver.driver_name).map((d) => (
                          <SelectItem key={d.driver_name} value={d.driver_name}>{d.driver_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Relationship Type</Label>
                    <Select value={newRelationship.relationship_type || 'linked'} onValueChange={(value) => setNewRelationship({ ...newRelationship, relationship_type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="linked">Linked</SelectItem>
                        <SelectItem value="leaseholder">Leaseholder</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="owner_operator">Owner Operator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={newRelationship.description || ''} onChange={(e) => setNewRelationship({ ...newRelationship, description: e.target.value })} placeholder="Optional" />
                  </div>
                  <div className="flex items-center gap-2"><Checkbox checked={newRelationship.can_view_roster ?? true} onCheckedChange={(checked) => setNewRelationship({ ...newRelationship, can_view_roster: checked as boolean })} /><Label>View roster</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={newRelationship.can_edit_roster ?? false} onCheckedChange={(checked) => setNewRelationship({ ...newRelationship, can_edit_roster: checked as boolean })} /><Label>Edit roster</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={newRelationship.can_view_payroll ?? false} onCheckedChange={(checked) => setNewRelationship({ ...newRelationship, can_view_payroll: checked as boolean })} /><Label>View payroll</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={newRelationship.can_view_driver_details ?? true} onCheckedChange={(checked) => setNewRelationship({ ...newRelationship, can_view_driver_details: checked as boolean })} /><Label>View details</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={newRelationship.can_edit_driver_details ?? false} onCheckedChange={(checked) => setNewRelationship({ ...newRelationship, can_edit_driver_details: checked as boolean })} /><Label>Edit details</Label></div>
                  <div className="flex justify-end gap-2 md:col-span-3">
                    <Button type="button" variant="outline" onClick={() => setShowRelationshipForm(false)}>Cancel</Button>
                    <Button type="button" onClick={addRelationship}>Save Link</Button>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto rounded border border-gray-800">
              <table className="w-full text-sm">
                <thead className="bg-gray-800/80 text-gray-300">
                  <tr>
                    <th className="text-left px-3 py-2">Relationship</th>
                    <th className="text-left px-3 py-2">Direction</th>
                    <th className="text-left px-3 py-2">Access</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {relationships.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-5 text-center text-gray-500">No linked drivers yet.</td></tr>
                  ) : relationships.map((rel) => {
                    const isParent = rel.parent_driver_name === driver.driver_name;
                    const otherDriver = isParent ? rel.child_driver_name : rel.parent_driver_name;
                    return (
                      <tr key={rel.id} className="border-t border-gray-800">
                        <td className="px-3 py-2 text-gray-200">{otherDriver}<div className="text-xs text-gray-500 capitalize">{rel.relationship_type}</div></td>
                        <td className="px-3 py-2 text-gray-400">{isParent ? 'This driver manages / links to them' : 'This driver is linked under them'}</td>
                        <td className="px-3 py-2 text-xs text-gray-300">
                          {rel.can_view_roster && <Badge className="mr-1 mb-1">Roster view</Badge>}
                          {rel.can_edit_roster && <Badge className="mr-1 mb-1">Roster edit</Badge>}
                          {rel.can_view_payroll && <Badge className="mr-1 mb-1">Payroll view</Badge>}
                          {rel.can_view_driver_details && <Badge className="mr-1 mb-1">Details view</Badge>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button type="button" variant="outline" size="sm" onClick={() => deleteRelationship(rel.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
          <Section title="Notes" subtitle="General notes and future document links">
            <div className="rounded-lg border border-dashed border-gray-700 bg-gray-800/30 p-4 text-sm text-gray-300">
              General driver notes and additional documents can be added here later. Roster notes currently live under the Roster tab.
            </div>
          </Section>
          </div>
          )}
        </div>

        <div className="flex gap-4 text-sm text-gray-400 mt-4 pt-4 border-t border-gray-700">
          <span>Created: {driver.created_at ? formatDate(driver.created_at) : '-'}</span>
          <span>Updated: {driver.updated_at ? formatDate(driver.updated_at) : '-'}</span>
        </div>

      </DialogContent>
    </Dialog>
    <BillableUnitsWarningDialog {...billableUnitsDialogProps} />
    </>
  );
};

export default DriverEditModal;
